import { styled } from '@mui/material/styles';
import { CustomContentProps, MaterialDesignContent } from 'notistack';
import { forwardRef, useEffect, useRef } from 'react';

// notistack v3 ships warning toasts as white-on-amber (~2.1:1 contrast),
// below WCAG AA. Compute a readable foreground from the warning background.
const ReadableContent = styled(MaterialDesignContent)(({ theme }) => ({
    '&.notistack-MuiContent-warning': {
        color: theme.palette.getContrastText(theme.palette.warning.main),
    },
}));

/**
 * Custom content component for the `warning` snackbar variant, registered on
 * the app-wide `SnackbarProvider`.
 *
 * Besides correcting the low-contrast default styling, it mirrors the warning
 * message to the developer console (`console.warn`) when the snackbar is
 * shown. Routing every warning through this component guarantees a console
 * trail for debugging without each call site having to log explicitly.
 *
 * A warning's on-screen text may embed user-provided values (e.g. skipped-file
 * names) that can contain PHI, which must never reach logs. Call sites that
 * include such data pass a PHI-free `logMessage` (see `~/utils/notifications`);
 * this component logs that redacted variant in preference to the user-facing
 * `message`.
 */
const WarningSnackbarContent = forwardRef<HTMLDivElement, CustomContentProps>(
    function WarningSnackbarContent(props, ref) {
        const { message, logMessage } = props;
        const consoleMessage = logMessage ?? message;

        // A snackbar's message is fixed for the life of the instance, so log
        // it exactly once. The ref guard survives React StrictMode's
        // intentional double-invocation of mount effects in development,
        // which would otherwise log every warning twice.
        const hasLogged = useRef(false);
        useEffect(() => {
            if (hasLogged.current || consoleMessage === undefined) {
                return;
            }
            hasLogged.current = true;
            console.warn(consoleMessage);
        }, [consoleMessage]);

        return <ReadableContent ref={ref} {...props} />;
    }
);

export default WarningSnackbarContent;
